/**
 * screens/BoardScreen.tsx — the Live Dashboard (the `board` screen).
 *
 * A dark, colour-accented, projection-grade real-time board, scoped to the
 * signed-in org (RLS: one org per session). This is the ONLY coloured screen;
 * the rest of the console is monochrome, so the palette lives locally here.
 *
 * Data sources (all REAL):
 *  - Live now + live callers + per-turn transcript → the shell SSE feed
 *    (useLiveCalls / liveCallsContext). No polling for these.
 *  - Header identity (name/dial/vertical/accent) → GET /v1/board/identity.
 *  - KPI numbers + domain counters → GET /v1/board/summary (today rollup).
 *  - Revenue is a STUB (backend returns 0) until per-action amounts are captured.
 *
 * Animation is pure view-layer over whatever real values arrive: numbers EASE
 * toward targets, a green flash blooms on increment, transcript types in. All of
 * it freezes under prefers-reduced-motion.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Platform, ScrollView } from 'react-native';
import { useConsole } from '../context/voiceConsoleContext';
import { useLiveCalls } from '../context/liveCallsContext';
import {
  getBoardSummary, BoardSummary,
} from '../services/voice/voiceService';
import { useBoardIdentity } from '../services/voice/boardIdentityStore';

// ── Board-only palette (design tokens for THIS screen; monochrome elsewhere) ──
// LED-wall tuned: near-black (not pure #000, which blooms on LED), off-white text
// (less halo than pure #fff), and lifted mutes so thin gray survives at distance.
const BG = '#0A0C10';
const PANEL = '#12151C';
const HAIR = 'rgba(255,255,255,.10)';
const TXT = '#F2F2F2';
const MUTED = '#B8BDC9';
const GREEN = '#4CB782';
const RED = '#EB5757';
const DEFAULT_ACCENT = '#2D5CF6';

// ── LED-wall scale. One knob for the whole screen. Target wall is P3, 10'×8',
// native 1016×812 (near-square) for a ~200-person room. Calibrated at that native
// res in fullscreen board mode (?board=1) — retune on-site by changing SCALE alone.
// (Was 2.1 for 1080p; at the true 1016-wide canvas that shattered tile labels and
// overflowed, so 1.5 fits one screen with the 2-column activity grid below.) ──
const SCALE = 1.5;
const fs = (n: number) => Math.round(n * SCALE);

// Two verticals only. Tile labels map to the metric keys the backend emits per
// SPINE/mutate tool (boardService ACTION_EVENTS). Only real counters show data.
type MetricDef = { key: string; label: string };
const HOSPITALITY_METRICS: MetricDef[] = [
  { key: 'reserved', label: 'Tables reserved' }, { key: 'orders', label: 'Orders placed' },
  // 'bills' (Bills finalized) + 'waitlist' (Waitlist joins) hidden on the board.
  { key: 'complaints', label: 'Complaints' }, { key: 'cancelled', label: 'Cancellations' },
];
const SALON_METRICS: MetricDef[] = [
  { key: 'booked', label: 'Appointments' }, { key: 'rescheduled', label: 'Reschedules' },
  // 'waitlist' (Waitlist joins) + 'withdrawn' (Waitlist withdrawals) hidden on the board.
  { key: 'cancelled', label: 'Cancellations' }, { key: 'complaints', label: 'Complaints' },
];
function metricsFor(vertical: string | null): { defs: MetricDef[]; revLabel: string } {
  if (vertical && /salon/i.test(vertical)) {
    return { defs: SALON_METRICS, revLabel: 'Services booked value' };
  }
  return { defs: HOSPITALITY_METRICS, revLabel: 'Order revenue' };
}

function hexA(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
const enIN = (n: number) => Math.round(n).toLocaleString('en-IN');
const money = (n: number) => '₹' + enIN(n);
function maskPhone(num: string): string {
  // Board shows the caller number partially masked (it's caller PII). Keep the
  // country/first digit + last two, dot the middle.
  const d = (num || '').replace(/[^\d]/g, '');
  if (d.length < 4) return num || 'unknown';
  return `+${d.slice(0, 2)} ${d[2] ?? ''}X•• ••${d.slice(-2)}`;
}

const prefersReducedMotion =
  Platform.OS === 'web' && typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

// ── Eased number: displays a value that glides toward `target` each frame, and
// flashes green for ~650ms whenever the target increases. Pure view-layer. ──
function useEased(target: number): { shown: number; flash: boolean } {
  const [shown, setShown] = useState(target);
  const [flash, setFlash] = useState(false);
  const disp = useRef(target);
  const tgt = useRef(target);
  const prevTarget = useRef(target);
  const flashUntil = useRef(0);

  useEffect(() => {
    tgt.current = target;
    if (target > prevTarget.current) flashUntil.current = Date.now() + 650;
    prevTarget.current = target;
    if (prefersReducedMotion) { disp.current = target; setShown(target); }
  }, [target]);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const id = setInterval(() => {
      const t = tgt.current, d = disp.current;
      if (d !== t) {
        const nd = d + (t - d) * 0.22;
        disp.current = Math.abs(t - nd) < 0.6 ? t : nd;
        setShown(disp.current);
      }
      const f = Date.now() < flashUntil.current;
      setFlash(prev => (prev !== f ? f : prev));
    }, 80);
    return () => clearInterval(id);
  }, []);

  return { shown, flash };
}

// ── A number cell that eases + flashes (KPI value, domain metric, revenue). ──
function EasedNum({ value, size, color, mono = true }: { value: number; size: number; color: string; mono?: boolean }) {
  const { shown, flash } = useEased(value);
  return (
    <Text
      style={{
        fontFamily: mono ? 'monospace' : undefined,
        fontSize: size, fontWeight: '700', color, lineHeight: size,
        ...(flash ? ({ backgroundColor: hexA(GREEN, 0.2), borderRadius: 8 } as any) : {}),
      }}
    >
      {enIN(shown)}
    </Text>
  );
}

// ── Constellation (web inline SVG, mirrors icon.tsx's web pattern). Renders live
// callers as glowing seats on concentric rings around the SK VOICE hub. ──
// 150 seats across concentric rings. Inner ring starts at r:26 to clear the
// center hub (a visible gap), outer ring at r:49 fills ~85%+ of the panel.
// Dot count grows outward (larger circumference → more dots) for even density.
const RINGS = [
  { r: 16, n: 6 }, { r: 22, n: 9 },  // inner rings — fill the space near the hub
  { r: 28, n: 12 }, { r: 34, n: 15 }, { r: 40, n: 18 },
  { r: 45, n: 20 }, { r: 49, n: 20 }, // 6+9+12+15+18+20+20 = 100
];
const RING_PLAN: { r: number; ang: number }[] = [];
RINGS.forEach((rg, ri) => { for (let k = 0; k < rg.n; k++) RING_PLAN.push({ r: rg.r, ang: -90 + k * (360 / rg.n) + ri * 9 }); });

function Constellation({ liveCount, accent, seats }: {
  liveCount: number; accent: string;
  seats: { active: boolean; spot: boolean; hold: boolean; label: string }[];
}) {
  if (Platform.OS !== 'web') {
    // Native fallback: a simple hub label (the projection board is web-only).
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ color: TXT, fontWeight: '700', fontSize: fs(13) }}>SK VOICE</Text>
        <Text style={{ color: MUTED, fontFamily: 'monospace', fontSize: fs(11) }}>{liveCount} live</Text>
      </View>
    );
  }
  const cx0 = 50, cy0 = 50;
  const nodes = seats.map((s, i) => {
    const pl = RING_PLAN[i] || { r: 46, ang: i * 15 };
    const a = (pl.ang * Math.PI) / 180;
    const cx = cx0 + pl.r * Math.cos(a), cy = cy0 + pl.r * Math.sin(a);
    return { ...s, cx, cy };
  });
  return (
    <div style={{
      // Square sized to the SMALLER of available width/height so it never drives
      // the flex row taller than it is (was width-only → grew on wide panels).
      position: 'relative', aspectRatio: '1' as any, margin: 'auto',
      width: 'auto', height: '100%', maxWidth: 'min(100%,620px)', maxHeight: '100%',
    }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
        {/* slowly-rotating dotted rings */}
        {RINGS.map((rg, i) => (
          <circle key={i} cx="50" cy="50" r={rg.r} fill="none" stroke={`rgba(255,255,255,${Math.max(0.03, 0.07 - i * 0.006).toFixed(3)})`}
            strokeWidth="0.3" strokeDasharray="1 2.5"
            style={{ transformOrigin: '50% 50%', animation: `sk-spin ${90 + i * 16}s linear infinite${i % 2 ? ' reverse' : ''}` }} />
        ))}
        {/* animated connectors: dashes flow center → active caller */}
        {nodes.filter(n => n.active).map((n, i) => (
          <line key={i} className="sk-conn" x1="50" y1="50" x2={n.cx.toFixed(2)} y2={n.cy.toFixed(2)}
            stroke={n.hold ? RED : accent} strokeWidth="0.35" strokeDasharray="1 1.6" opacity="0.7" />
        ))}
      </svg>
      {/* center agent hub — MOVING equalizer bars (agent speaking) */}
      <div style={{
        position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)',
        width: 108, height: 108, borderRadius: '50%', display: 'grid', placeItems: 'center', textAlign: 'center',
        background: `radial-gradient(circle at 50% 40%, ${hexA(accent, 0.28)}, ${PANEL} 72%)`,
        boxShadow: `0 0 0 1px rgba(255,255,255,.10), 0 0 40px ${hexA(accent, liveCount > 0 ? 0.45 : 0.18)}`, zIndex: 5,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
          <span style={{ display: 'inline-flex', gap: 2.5, alignItems: 'flex-end', height: 18 }}>
            {[0, 0.15, 0.3, 0.45, 0.6].map((d, i) => (
              <span key={i} className="sk-eq-bar" style={{ width: 3, height: 18, borderRadius: 2, background: accent, animationDelay: `${d}s` }} />
            ))}
          </span>
          <div style={{ fontSize: fs(10), fontWeight: 700, letterSpacing: '.06em', color: TXT }}>SK VOICE</div>
          <div style={{ fontFamily: 'monospace', fontSize: fs(9), color: MUTED }}>{liveCount} live</div>
        </div>
      </div>
      {/* caller seats — blinking dot + pulsing ring + redacted number label */}
      {nodes.map((n, i) => {
        const col = n.hold ? RED : accent;
        const sz = n.spot ? 16 : 11;
        if (!n.active) {
          return (
            <div key={i} style={{
              position: 'absolute', left: `${n.cx.toFixed(2)}%`, top: `${n.cy.toFixed(2)}%`,
              transform: 'translate(-50%,-50%)', width: 7, height: 7, borderRadius: '50%',
              background: '#242833', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.05)', zIndex: 1,
            }} />
          );
        }
        return (
          <React.Fragment key={i}>
            {/* expanding pulse ring */}
            <span className="sk-ring-pulse" style={{
              position: 'absolute', left: `${n.cx.toFixed(2)}%`, top: `${n.cy.toFixed(2)}%`,
              transform: 'translate(-50%,-50%)', width: sz + 8, height: sz + 8, borderRadius: '50%',
              border: `1.5px solid ${col}`, pointerEvents: 'none', zIndex: 2,
            }} />
            {/* blinking caller dot */}
            <div className="sk-dot-pulse" style={{
              position: 'absolute', left: `${n.cx.toFixed(2)}%`, top: `${n.cy.toFixed(2)}%`,
              transform: 'translate(-50%,-50%)', width: sz, height: sz, borderRadius: '50%',
              display: 'grid', placeItems: 'center', color: '#0A0C10', zIndex: n.spot ? 4 : 3,
              background: col, boxShadow: `0 0 0 ${n.spot ? 3 : 2}px ${hexA(col, 0.18)}, 0 0 ${n.spot ? 16 : 9}px ${hexA(col, 0.6)}`,
            }}>
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="3.4" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
              </svg>
            </div>
            {/* redacted number label under the dot */}
            {!!n.label && (
              <span style={{
                position: 'absolute', left: `${n.cx.toFixed(2)}%`, top: `${n.cy.toFixed(2)}%`,
                transform: `translate(-50%, ${n.spot ? 16 : 12}px)`, fontFamily: 'monospace', fontSize: 8.5,
                color: '#5AA9FF', fontWeight: 600, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 4,
              }}>{n.label}</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function BoardScreen() {
  const { tenant, orgId, liveTick } = useConsole();
  const { calls: liveCalls, connected, transcriptFor, callerFeed, actionTicker } = useLiveCalls();

  // Identity comes from the shared store: prefetched at session-resolve time and
  // cached per-org, so the header paints the real number on the first frame
  // instead of sitting at "—" for a round-trip. Summary still polls ~2s for the
  // today rollup (live-now and transcript are pushed via SSE, so only the
  // aggregate counters need polling).
  const identity = useBoardIdentity(orgId);
  const [summary, setSummary] = useState<BoardSummary>({ live: 0, callsHandled: 0, actionsCompleted: 0, revenue: 0, metrics: {} });

  const loadSummary = useCallback(async () => {
    const s = await getBoardSummary();
    setSummary(s.data);
  }, []);
  useEffect(() => {
    void loadSummary();
    const id = setInterval(() => { void loadSummary(); }, 2000);
    return () => { clearInterval(id); };
  }, [orgId, loadSummary]);

  const accent = identity?.accent || DEFAULT_ACCENT;
  const vertical = identity?.vertical ?? tenant.vertical;
  const orgName = identity?.orgName || tenant.name || 'Your organisation';
  const { defs, revLabel } = useMemo(() => metricsFor(vertical), [vertical]);

  // Live-now is DERIVED from the SSE feed (authoritative), not the summary stub.
  void liveTick; // reading the 1s tick forces elapsed/clock recompute each second
  const liveCount = liveCalls.length;

  // Clock + date (client-side). Date and time render as a separated cluster:
  // weekday over full date on the left, a hairline divider, then the big time.
  const now = new Date();
  const clock = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const weekday = now.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase();
  const dateFull = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  // Seat model: scatter live callers across RANDOM seats (not filled inner-first).
  // Each call maps to a STABLE seat from a hash of its id, so a caller keeps its
  // dot across re-renders; on collision we probe forward to the next free seat.
  const seats = useMemo(() => {
    const N = 100;
    const arr = Array.from({ length: N }, () => ({ active: false, spot: false, hold: false, label: '' }));
    const hash = (s: string) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; };
    liveCalls.slice(0, N).forEach((c, i) => {
      let seat = hash(c.id) % N;
      while (arr[seat].active) seat = (seat + 1) % N; // linear-probe past taken seats
      const digits = (c.num || '').replace(/[^\d]/g, '');
      arr[seat] = { active: true, spot: i === 0, hold: false, label: digits ? '••••' + digits.slice(-4) : '' };
    });
    return arr;
  }, [liveCalls]);

  return (
    <View style={{ flex: 1, height: '100%', backgroundColor: BG }}>
      {/* header band — tight vertical padding so the whole board fits the wall's
          812px height without scrolling (native 1016×812, no chrome). */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 20, paddingVertical: 8, paddingHorizontal: 28, borderBottomWidth: 1, borderColor: HAIR }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <View style={{ width: 12, height: 12, borderRadius: 999, backgroundColor: RED }} />
          <Text style={{ fontWeight: '700', fontSize: fs(19), color: TXT, letterSpacing: 0.4 }}>SK VOICE</Text>
        </View>
        <View style={{ flex: 1, alignItems: 'center' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            {/* org name to the LEFT of the accent dot: "Naturals ·" + dial number */}
            <Text style={{ fontSize: fs(15), fontWeight: '700', color: accent, letterSpacing: 0.3 }}>{orgName} ·</Text>
            <View style={{ width: 11, height: 11, borderRadius: 999, backgroundColor: accent }} />
            <Text style={{ fontFamily: 'monospace', fontSize: fs(34), fontWeight: '700', color: TXT, lineHeight: fs(38), letterSpacing: 1 }}>
              {identity?.dialNumber ? (/^0|^\+/.test(identity.dialNumber) ? identity.dialNumber : '0' + identity.dialNumber) : '—'}
            </Text>
          </View>
        </View>
        {/* date | time cluster — date stacked on the left, hairline divider, big
            time on the right; connection state as a small dot beside the label. */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: fs(12) }}>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={{ fontFamily: 'monospace', fontSize: fs(9), fontWeight: '700', letterSpacing: 2, color: accent }}>{weekday}</Text>
            <Text style={{ fontFamily: 'monospace', fontSize: fs(12), fontWeight: '600', color: TXT, marginTop: 1 }}>{dateFull}</Text>
          </View>
          <View style={{ width: 1, height: fs(30), backgroundColor: HAIR }} />
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={{ fontFamily: 'monospace', fontSize: fs(22), fontWeight: '700', color: TXT, letterSpacing: 1 }}>{clock}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 1 }}>
              <View style={{ width: fs(5), height: fs(5), borderRadius: 999, backgroundColor: connected ? GREEN : RED }} />
              <Text style={{ fontSize: fs(9), fontWeight: '600', letterSpacing: 0.5, color: MUTED }}>{connected ? 'REAL-TIME' : 'RECONNECTING…'}</Text>
            </View>
          </View>
        </View>
      </View>

      {/* body */}
      <View style={{ flex: 1, minHeight: 0, paddingVertical: 10, paddingHorizontal: 28, gap: 10 }}>
        {/* hero KPIs */}
        <View style={{ flexDirection: 'row', gap: 14 }}>
          <KpiTile label="Live now" accent={RED} value={liveCount} />
          <KpiTile label="Calls handled" accent={accent} value={summary.callsHandled} />
          <KpiTile label="Actions completed" accent={GREEN} value={summary.actionsCompleted} />
        </View>

        {/* activity panel + constellation */}
        <View style={{ flex: 1, minHeight: 0, flexDirection: 'row', gap: 16 }}>
          {/* Today's activity (narrower — constellation gets the room) */}
          <View style={{ flex: 0.8, minHeight: 0, borderWidth: 1, borderColor: HAIR, borderRadius: 16, backgroundColor: PANEL, padding: 14, borderTopWidth: 3, borderTopColor: accent }}>
            <View style={{ marginBottom: 10 }}>
              <Text style={{ fontSize: fs(13), fontWeight: '600', letterSpacing: 0.5, color: MUTED }}>TODAY'S ACTIVITY</Text>
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
              {defs.map(d => (
                <View key={d.key} style={{ width: '48%', borderWidth: 1, borderColor: 'rgba(255,255,255,.06)', borderRadius: 10, backgroundColor: 'rgba(255,255,255,.02)', padding: 11 }}>
                  <EasedNum value={summary.metrics[d.key] ?? 0} size={fs(22)} color={accent} />
                  <Text style={{ fontSize: fs(10.5), color: '#c2c7d2', marginTop: 4 }}>{d.label}</Text>
                </View>
              ))}
            </View>
            {/* live transcript — scrolling log of every turn from all callers,
                newest first, growing as the conversations go on (masked number +
                intent chip). SUKI green, caller number blue. */}
            {/* flex:1 + a ScrollView so the transcript FILLS the panel and scrolls
                when it overflows — no dead space below when there are few lines,
                no cut-off when there are many. */}
            {/* flexBasis:0 + minHeight:0 forces the wrapper to derive its height
                from the flex row (not from its content), so the ScrollView caps
                and scrolls instead of the panel growing with the feed. */}
            <View style={{ flex: 1, flexBasis: 0, minHeight: 60, marginTop: 8 }}>
              <Text style={{ fontSize: fs(10), fontWeight: '600', letterSpacing: 0.5, color: MUTED, marginBottom: 6 }}>LIVE TRANSCRIPT</Text>
              <ScrollView style={{ flex: 1, minHeight: 0, ...(Platform.OS === 'web' ? { overflowY: 'auto' as any } : null) }} contentContainerStyle={{ paddingBottom: 4 }} showsVerticalScrollIndicator={false}>
                {callerFeed.slice(0, 40).map((ln, i) => (
                  <View key={ln.callId + ':' + ln.sp + ':' + ln.at + ':' + i} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: fs(3) }}>
                    {/* speaker: SUKI in green, caller's redacted number in blue.
                        width scales with the type so the two columns stay aligned. */}
                    <Text style={{ fontFamily: 'monospace', fontSize: fs(10), fontWeight: '700', flexShrink: 0, width: fs(104), color: ln.sp === 'agent' ? GREEN : '#7CC0FF' }}>
                      {ln.sp === 'agent' ? 'SK VOICE' : maskPhone(ln.num)}
                    </Text>
                    {!!ln.intent && (
                      <Text style={{ fontFamily: 'monospace', fontSize: fs(8), color: accent, borderWidth: 1, borderColor: accent, borderRadius: 4, paddingHorizontal: 5, flexShrink: 0 }}>
                        {ln.intent.replace(/_/g, ' ')}
                      </Text>
                    )}
                    <Text numberOfLines={1} style={{ flex: 1, fontSize: fs(11), color: ln.partial ? MUTED : '#E4E7EE' }}>
                      {ln.text}{ln.partial ? ' ▍' : ''}
                    </Text>
                  </View>
                ))}
                {callerFeed.length === 0 && (
                  <Text style={{ fontSize: fs(11), color: MUTED }}>No live calls — dial the number to see it work.</Text>
                )}
              </ScrollView>
            </View>
            {/* revenue (STUB — backend returns 0 until per-action amounts exist) */}
            <View style={{ marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderColor: HAIR, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Text style={{ fontSize: fs(11), color: MUTED, letterSpacing: 0.5 }}>{revLabel.toUpperCase()}</Text>
              <Text style={{ fontFamily: 'monospace', fontSize: fs(28), fontWeight: '600', color: GREEN }}>{money(summary.revenue)}</Text>
            </View>
          </View>

          {/* Live callers constellation (bigger) */}
          <View style={{ flex: 2, borderWidth: 1, borderColor: HAIR, borderRadius: 16, backgroundColor: PANEL, overflow: 'hidden' }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 14, paddingHorizontal: 16 }}>
              <Text style={{ fontWeight: '600', fontSize: fs(14), color: TXT }}>Live callers</Text>
              <Text style={{ fontSize: fs(10.5), color: MUTED }}>{connected ? 'streaming' : 'offline'}</Text>
            </View>
            <View style={{ flex: 1, minHeight: 0, justifyContent: 'center', alignItems: 'center', padding: 6 }}>
              <Constellation liveCount={liveCount} accent={accent} seats={seats} />
            </View>
          </View>
        </View>
      </View>

      {/* action ticker — live scrolling marquee of completed actions (SSE).
          The list is duplicated so the -50% translate loops seamlessly. */}
      <View style={{ height: fs(30), borderTopWidth: 1, borderColor: HAIR, backgroundColor: '#0d1016', flexDirection: 'row', alignItems: 'center', overflow: 'hidden' }}>
        <View style={{ height: '100%', justifyContent: 'center', paddingHorizontal: 16, backgroundColor: PANEL, borderRightWidth: 1, borderColor: HAIR, zIndex: 2 }}>
          <Text style={{ fontSize: fs(10.5), fontWeight: '600', letterSpacing: 0.6, color: GREEN }}>✓ ACTIONS</Text>
        </View>
        {actionTicker.length > 0 ? (
          Platform.OS === 'web' ? (
            <div className="sk-ticker" style={{ display: 'flex', alignItems: 'center', gap: 32, whiteSpace: 'nowrap', paddingLeft: 24 }}>
              {actionTicker.concat(actionTicker).map((t, i) => (
                <span key={t.id + ':' + i} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: fs(13), color: '#E4E7EE' }}>
                  <span style={{ width: 7, height: 7, borderRadius: 999, background: accent }} />{t.label}
                </span>
              ))}
            </div>
          ) : (
            <Text style={{ marginLeft: 24, fontSize: fs(13), color: MUTED }}>{actionTicker[0]?.label}</Text>
          )
        ) : (
          <Text style={{ marginLeft: 24, fontSize: fs(13), color: MUTED }}>
            {summary.actionsCompleted > 0 ? `${enIN(summary.actionsCompleted)} actions completed today` : 'Completed actions appear here as calls resolve.'}
          </Text>
        )}
      </View>
    </View>
  );
}

function KpiTile({ label, accent, value }: { label: string; accent: string; value: number }) {
  return (
    <View style={{ flex: 1, borderWidth: 1, borderColor: HAIR, borderRadius: 14, backgroundColor: PANEL, padding: 16 }}>
      <Text style={{ fontSize: fs(11), color: accent, fontWeight: '700', letterSpacing: 0.5, marginBottom: 8 }}>{label.toUpperCase()}</Text>
      <EasedNum value={value} size={fs(40)} color={accent} />
    </View>
  );
}
